import React, { useState, useCallback, useEffect, createContext, useContext } from "react";
import { getDocumentParagraphs, getFromStorage } from "@/taskpane/lib";
import {
  resetRiskAnalysisCanceledTokens,
  riskAnalysisCancelTokens,
  runRiskAnalysis,
} from "@/services/risk-analysis-service";
import {
  AnalysisResult,
  ClauseData,
  DocumentAnalysis,
  DocumentClassification,
  DocumentPrompts,
  Playbook,
  PromptConfigData,
} from "@/types/risk-analysis";
import {
  deletePlaybook,
  duplicatePlaybook,
  getPlaybooks,
  createPlaybook,
  updatePlaybook,
} from "@/services/playbook-service";
import { useAuth } from "@/context/auth";
import { trackEvent } from "@/services/analytics-service";
import { AnalyticsKey } from "@/types/analytics";
import { AuthData } from "@/types/auth";
import { getAllPromptConfigs } from "@/services/prompt-config-service";
import { ValidationError } from "@/types/validation";
import axios from "axios";
import {
  deleteAllClassificationDataFromDocumentStorage,
  DOCUMENT_STORAGE_KEY,
  getFromDocumentStorage,
  saveToDocumentStorage,
} from "@/taskpane/lib/common/document-storage";
import { DocumentParagraph } from "@/types/document";
import log from "@/utils/logger";
import { Performance } from "@/utils/performance";
import { DEFAULT_CUSTOMER_RULE_ID, STORAGE_KEY_PREFIX } from "@/constants";

type DocumentClassificationContextType = {
  errors: ValidationError<"promptConfig" | "playbook">[];
  classifications: DocumentClassification[];
  setClassifications: React.Dispatch<React.SetStateAction<DocumentClassification[]>>;
  refreshAnalysis: (playbookRuleId?: DocumentClassification["rules"][number]["id"]) => Promise<void>;
  promptsUsed: DocumentPrompts[];
  getAnalysisClassification: () => Promise<void>;
  promptConfigs: PromptConfigData[];
  selectedPromptConfig: PromptConfigData | null;
  setSelectedPromptConfig: (promptConfig: PromptConfigData | null) => void;
  fetchPromptConfigs: () => void;
  isFetchingPromptConfigs: boolean;
  handleClearData: () => void;
  handleCancel: () => void;
  playbooks: (Playbook | undefined)[];
  setPlaybooks: React.Dispatch<React.SetStateAction<Playbook[]>>;
  sortPlaybooksBy: SortPlaybooksBy;
  setSortPlaybooksBy: React.Dispatch<React.SetStateAction<SortPlaybooksBy>>;
  sortPlaybooks: (sortBy: SortPlaybooksBy) => void;
  fetchPlaybooks: () => Promise<Playbook[]>;
  fetchingPlaybooks: boolean;
  selectedPlaybook: Playbook;
  setSelectedPlaybook: React.Dispatch<React.SetStateAction<Playbook>>;
  currentClassification: DocumentClassification | undefined;
  addPlaybook: (name: string, isPrivate: boolean, isEditable: boolean, playbookRules: ClauseData[]) => Promise<void>;
  copyPlaybook: (
    id: string,
    name: string,
    isPrivate: boolean,
    isEditable: boolean,
    originalId?: string
  ) => Promise<void>;
  editPlaybook: (
    id: string,
    name: string,
    isPrivate: boolean,
    isEditable: boolean,
    playbookRules: ClauseData[]
  ) => Promise<void>;
  removePlaybook: (id: string) => Promise<void>;
};

const sortStorageKey = `${STORAGE_KEY_PREFIX}sortPlaybooksBy`;

export const SORT_PLAYBOOKS_OPTIONS = {
  "name-asc": "Name (A-Z)",
  "name-desc": "Name (Z-A)",
  "created-desc": "Newest created",
  "created-asc": "Oldest created",
  "updated-desc": "Newest updated",
  "updated-asc": "Oldest updated",
} as const;
export type SortPlaybooksBy = keyof typeof SORT_PLAYBOOKS_OPTIONS;

const WHITE_SPACE_REGEX = /\s+/g; // Matches any whitespace characters
const NEW_LINE_REGEX = /(?:\r\n|\r|\n)+/g; // Matches any combination of newline characters
const WORD_LIST_NUMBERING_REGEXES = [
  /^\d+\.\s*/, // Matches numbered lists followed by a period. Examples: "1. ", "42. "
  /^[a-zA-Z]{1,}\.\s*/, // Matches letter-based lists (single or multiple letters) followed by a period. Examples: "a. ", "A. ", "ab. ", "AB. "
  /^\d+\)\s*/, // Matches numbered lists followed by a closing parenthesis. Examples: "1) ", "42) "
  /^[a-zA-Z]{1,}\)\s*/, // Matches letter-based lists followed by a closing parenthesis. Examples: "a) ", "b) ", "ab) "
  /^\(\d+\)\s*/, // Matches numbered lists with digits enclosed in parentheses. Examples: "(1) ", "(42) "
  /^\([a-zA-Z]+\)\s*/, // Matches letter-based lists (single or multiple letters) enclosed in parentheses. Examples: "(a) ", "(B) ", "(Ab) "
  /^(\(?[a-zA-Z0-9]+\)?[.)]){2,}\.?\s*/, // Matches multi-level numbering systems, including combinations of letters, numbers, and symbols. Examples: "1.1. ", "A.1. ", "1.a) ", "(1)(a) ", "1.2.3. "
  /^\s*[•◦*-●○■□◆◇★☆▪▫►▶▸▹]\s+/, // Matches any one of the common bullet characters. Examples: "• item", "▪ item", "★ item"
];

function cleanWordListNumbering(text: string) {
  for (const regex of WORD_LIST_NUMBERING_REGEXES) {
    if (regex.test(text)) {
      text = text.replace(regex, "");
      break; // Only apply the first matching pattern
    }
  }
  return text;
}

function areArraysDifferent(arr1: string[], arr2: string[]) {
  const set1 = new Set(arr1);
  const set2 = new Set(arr2);
  if (arr1.some((value) => !set2.has(value))) return true;
  if (arr2.some((value) => !set1.has(value))) return true;
  return false;
}

function getSortedPlaybooks(playbooks: Playbook[], sortBy: SortPlaybooksBy) {
  return [...playbooks].sort((a, b) => {
    switch (sortBy) {
      case "name-asc":
        return a.name.localeCompare(b.name);
      case "name-desc":
        return b.name.localeCompare(a.name);
      case "created-desc":
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case "created-asc":
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case "updated-desc":
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      case "updated-asc":
        return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      default:
        return 0;
    }
  });
}

const DocumentClassificationContext = createContext<DocumentClassificationContextType | null>(null);

export const DocumentClassificationProvider = ({ children }: { children: React.ReactNode }) => {
  const [classifications, setClassifications] = useState<DocumentClassification[]>([]);
  const [promptsUsed, setPromptsUsed] = useState<DocumentPrompts[]>([]);
  const [isFetchingPromptConfigs, setIsFetchingPromptConfigs] = useState(false);
  const [promptConfigs, setPromptConfigs] = useState<PromptConfigData[]>([]);
  const [errors, setErrors] = useState<ValidationError<"promptConfig" | "playbook">[]>([]);
  const [selectedPlaybook, setSelectedPlaybook] = useState<Playbook>();
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [sortPlaybooksBy, setSortPlaybooksBy] = useState<SortPlaybooksBy>(
    (localStorage.getItem(sortStorageKey) || "created-desc") as SortPlaybooksBy
  );
  const [fetchingPlaybooks, setFetchingPlaybooks] = useState(false);
  const [selectedPromptConfig, setSelectedPromptConfig] = useState<PromptConfigData | null>(null);
  const { user } = useAuth();
  const currentClassification = classifications.find(
    (classification) => classification.playbookId === selectedPlaybook?.id
  );

  const fetchPromptConfigs = useCallback(async () => {
    try {
      setErrors((prev) => prev.filter((error) => error.field !== "promptConfig"));
      const storageUser = await getFromStorage("user");
      const user: AuthData = JSON.parse(storageUser || "{}");
      if (!user?.user?.id) return;

      setIsFetchingPromptConfigs(true);
      const configs = await getAllPromptConfigs(user);
      setPromptConfigs(configs);
      setSelectedPromptConfig(configs.find((config) => config.useAsDefault) || configs[0]);
    } catch (error) {
      log.error("Error fetching prompt configs", undefined, error);
      setErrors((prev) => [...prev, { field: "promptConfig", message: "Error fetching prompt configs" }]);
    } finally {
      setIsFetchingPromptConfigs(false);
    }
  }, [user]);

  const fetchPlaybooks = useCallback(async () => {
    if (!user?.user?.id) return [];
    setFetchingPlaybooks(true);
    setErrors((prev) => prev.filter((error) => error.field !== "playbook"));
    const storageUser = await getFromStorage("user");
    const parsedUser: AuthData = JSON.parse(storageUser || "{}");
    try {
      const playbooks = await getPlaybooks(parsedUser);
      const updatedPlaybooks = playbooks.map((playbook) => ({
        ...playbook,
        rules: playbook.rules.map((rule) => {
          const customerRules = rule.customerRules ?? [];
          return {
            ...rule,
            selectedCustomerRuleId: DEFAULT_CUSTOMER_RULE_ID,
            customerRules:
              customerRules.length > 0
                ? [
                    {
                      id: DEFAULT_CUSTOMER_RULE_ID,
                      playbookRuleId: rule.id,
                      title: rule.preferredLanguageTitle || "Default rule",
                      prompt: rule.preferredLanguagePrompt ?? "",
                      rule: rule.preferredLanguage,
                      example: "",
                      order: 0,
                      createdAt: new Date(rule.createdAt),
                      updatedAt: new Date(rule.updatedAt),
                    },
                    ...customerRules,
                  ]
                : [],
          };
        }),
      }));
      const sortedPlaybooks = getSortedPlaybooks(updatedPlaybooks, sortPlaybooksBy);
      setPlaybooks(sortedPlaybooks);
      const previousSelectedPlaybook = sortedPlaybooks.find(
        (playbook) => playbook.id === currentClassification?.playbookId
      );
      setSelectedPlaybook(previousSelectedPlaybook || sortedPlaybooks[0]);

      // playbooks rules might have changed so we need to update classifications accordingly,
      // this means setting the analysis to null of the changed rule and update all the other fields,
      // we can use rule.updatedAt to check if the rule has changed
      setClassifications((prev) => {
        const updatedClassifications = prev.map((classification) => ({
          ...classification,
          rules: classification.rules.map((rule) => {
            const updatedRule = sortedPlaybooks
              .find((playbook) => playbook.id === classification.playbookId)
              ?.rules.find((r) => r.id === rule.id);
            if (updatedRule?.updatedAt === rule.updatedAt) {
              return rule;
            }
            return {
              ...updatedRule,
              analysis: null,
              isLoading: false,
              paragraphs: [],
              viewed: false,
              isCanceled: true,
              validationErrors: [],
            };
          }),
        }));
        saveToDocumentStorage(DOCUMENT_STORAGE_KEY.CLASSIFICATIONS, JSON.stringify(updatedClassifications));
        return updatedClassifications;
      });

      return sortedPlaybooks;
    } catch (error) {
      log.error("Error fetching playbook data", undefined, error);
      setErrors((prev) => [...prev, { field: "playbook", message: "Error fetching playbooks" }]);
    } finally {
      setFetchingPlaybooks(false);
    }
    return [];
  }, [setFetchingPlaybooks, setPlaybooks, setErrors, user, currentClassification]);

  const addPlaybook = async (name: string, isPrivate: boolean, isEditable: boolean, playbookRules: ClauseData[]) => {
    const storageUser = await getFromStorage("user");
    const parsedUser = JSON.parse(storageUser || "{}") as AuthData;
    await createPlaybook(parsedUser, name, isPrivate, isEditable, playbookRules);
    await handleInit();
  };

  const copyPlaybook = async (
    id: string,
    name: string,
    isPrivate: boolean,
    isEditable: boolean,
    originalId?: string
  ) => {
    const storageUser = await getFromStorage("user");
    const parsedUser = JSON.parse(storageUser || "{}") as AuthData;
    await duplicatePlaybook(parsedUser, id, name, isPrivate, isEditable, originalId);
    await handleInit();
  };

  const editPlaybook = async (
    id: string,
    name: string,
    isPrivate: boolean,
    isEditable: boolean,
    playbookRules: ClauseData[]
  ) => {
    const storageUser = await getFromStorage("user");
    const parsedUser = JSON.parse(storageUser || "{}") as AuthData;
    await updatePlaybook(parsedUser, id, name, isPrivate, isEditable, playbookRules);
    await handleInit();
  };

  const removePlaybook = async (id: string) => {
    const storageUser = await getFromStorage("user");
    const parsedUser = JSON.parse(storageUser || "{}") as AuthData;
    await deletePlaybook(parsedUser, id);
    await handleInit();
  };

  const sortPlaybooks = useCallback(
    (sortBy: SortPlaybooksBy) => {
      setSortPlaybooksBy(sortBy);
      localStorage.setItem(sortStorageKey, sortBy);
      setPlaybooks(getSortedPlaybooks(playbooks, sortBy));
    },
    [playbooks]
  );

  const getAnalysisResults = async (
    playbookItem: ClauseData,
    paragraphs: Array<DocumentParagraph>
  ): Promise<{ result: AnalysisResult; errors: (keyof AnalysisResult)[] }> => {
    trackEvent(AnalyticsKey.RISK_ANALYSIS.LABELING.START, {
      playbookItem,
      userId: user.user.id,
    });

    return runRiskAnalysis(
      playbookItem.id,
      playbookItem.selectedCustomerRuleId,
      selectedPromptConfig.id,
      user.user.id,
      paragraphs
    );
  };

  const runAnalysis = async (playbookItem: ClauseData) => {
    log.debug("runAnalysis called", "runAnalysis", { playbookItem, userId: user.user.id });
    trackEvent(AnalyticsKey.RISK_ANALYSIS.CLAUSE_ANALYSIS.START, {
      playbookItem,
      userId: user.user.id,
    });
    const performanceProcessId = Performance.startProcess("runAnalysis");

    try {
      const paragraphsRaw = await getFromDocumentStorage(DOCUMENT_STORAGE_KEY.PARAGRAPHS);
      log.debug("Fetched paragraphs from storage", "runAnalysis", { paragraphsRaw });
      const paragraphs = JSON.parse(paragraphsRaw) as DocumentParagraph[];
      log.debug("Parsed paragraphs", "runAnalysis", { paragraphs });

      const analysisResults = await getAnalysisResults(playbookItem, paragraphs);
      log.debug("Analysis results", "runAnalysis", { analysisResults });

      const { result, errors } = analysisResults;
      const { summary, valueExtraction, fullAnalysis, stoplight, redlines, paragraphsToAnalyze = [] } = result;

      // parse bullet characters and numbering
      redlines.data = redlines.data.map((redline) => {
        // @IMPORTANT: this sometimes fails
        const originalChunkParagraph = paragraphs.find((p) => p.id === redline?.originalChunk?.id);
        return {
          ...redline,
          // adding the original dirty text (original text with deleted tracked changes)
          originalChunk: {
            ...redline.originalChunk,
            _paragraph: originalChunkParagraph,
            text: cleanWordListNumbering(redline.originalChunk.text)
              .replace(NEW_LINE_REGEX, "")
              .replace(WHITE_SPACE_REGEX, " ")
              .trim(),
            fullText: cleanWordListNumbering(originalChunkParagraph?.fullText ?? "")
              .replace(WHITE_SPACE_REGEX, " ")
              .trim(),
          },
          pieces: redline.pieces.map((piece) => {
            if (piece.changeType === "add") {
              return {
                ...piece,
                originalSentence: cleanWordListNumbering(piece.originalSentence)
                  .replace(NEW_LINE_REGEX, "")
                  .replace(WHITE_SPACE_REGEX, " ")
                  .trim(),
                insertedText: cleanWordListNumbering(piece.insertedText),
              };
            }
            return {
              ...piece,
              originalSentence: cleanWordListNumbering(piece.originalSentence).replace(WHITE_SPACE_REGEX, " ").trim(),
              replaceWith: cleanWordListNumbering(piece.replaceWith)
                .replace(NEW_LINE_REGEX, "")
                .replace(WHITE_SPACE_REGEX, " ")
                .trim(),
            };
          }),
        };
      });
      log.debug("Redlines after cleaning", "runAnalysis", { redlines });

      trackEvent(AnalyticsKey.RISK_ANALYSIS.CLAUSE_ANALYSIS.FINISH, {
        playbookItem,
        userId: user.user.id,
        analysisResult: result,
      });

      // adding the full text (original text with deleted tracked changes)
      const paragraphsToAnalyzeWithDirtyText = paragraphsToAnalyze
        .map((p) => {
          const documentParagraph = paragraphs.find((op) => op.id === p.id);
          if (!documentParagraph) {
            log.debug("No document paragraph", "runAnalysis", { p, paragraphs });
            return null;
          }
          return {
            ...p,
            ...documentParagraph,
            fullText: cleanWordListNumbering(documentParagraph.fullText),
            text: cleanWordListNumbering(p.text),
          };
        })
        .filter(Boolean);
      log.debug("Paragraphs to analyze with dirty text", "runAnalysis", { paragraphsToAnalyzeWithDirtyText });

      const fallbackRule = (playbookItem.customerRules ?? []).find(
        (rule) => rule.id === playbookItem.selectedCustomerRuleId
      );
      const analysis: DocumentAnalysis = {
        clause: {
          name: playbookItem?.clauseTopic,
          description: playbookItem?.clauseTopicDescription,
          preferredLanguage: fallbackRule?.rule || playbookItem?.preferredLanguage,
          redlines: redlines?.data,
          fullReport: fullAnalysis?.data,
          summary: summary?.data,
          resultStatus: {
            message: valueExtraction?.data,
            color: stoplight?.data,
          },
        },
        highlights: [],
      };
      log.debug("Constructed analysis object", "runAnalysis", { analysis });

      const validationErrors: ValidationError<keyof AnalysisResult>[] = [];
      if (errors.length) {
        validationErrors.push(...errors.map((error) => ({ field: error, message: `Error during ${error}` })));
        log.warn("Validation errors found during analysis", "runAnalysis", { errors });
      }

      setClassifications((prev) => {
        const updatedClassification = prev.map((classification) =>
          classification.playbookId === playbookItem.playbookId
            ? {
                ...classification,
                rules: classification.rules.map((rule) =>
                  rule.id === playbookItem.id
                    ? {
                        ...rule,
                        isLoading: false,
                        analysis,
                        paragraphs: paragraphsToAnalyzeWithDirtyText,
                        validationErrors,
                        isCanceled: false,
                      }
                    : rule
                ),
              }
            : classification
        );
        log.debug("Updated classifications after analysis", "runAnalysis", { updatedClassification });
        saveToDocumentStorage(DOCUMENT_STORAGE_KEY.CLASSIFICATIONS, JSON.stringify(updatedClassification));
        return updatedClassification;
      });

      setPromptsUsed((prev) => {
        const updatedPromptsUsed: DocumentPrompts[] = prev.map((promptUsed) =>
          promptUsed.playbookId === playbookItem.playbookId
            ? {
                ...promptUsed,
                rules: promptUsed.rules.map((rule) =>
                  rule.id === playbookItem.id
                    ? {
                        ...rule,
                        summary: summary?.promptUsed,
                        fullAnalysis: fullAnalysis?.promptUsed,
                        valueExtraction: valueExtraction?.promptUsed,
                        stoplight: stoplight?.promptUsed,
                        redlines: redlines?.promptUsed,
                        clauseTopic: playbookItem.clauseTopic,
                        label: { system: [], user: [] },
                      }
                    : rule
                ),
              }
            : promptUsed
        );
        log.debug("Updated prompts used after analysis", "runAnalysis", { updatedPromptsUsed });
        saveToDocumentStorage(DOCUMENT_STORAGE_KEY.PROMPTS_USED, JSON.stringify(updatedPromptsUsed));
        return updatedPromptsUsed;
      });
    } catch (e) {
      if (axios.isCancel(e)) {
        log.info("Analysis cancelled by user", "runAnalysis");
        throw e;
      }
      log.error("Error running analysis", "runAnalysis", e);
      setClassifications((prev) =>
        prev.map((classification) =>
          classification.playbookId === playbookItem.playbookId
            ? {
                ...classification,
                rules: classification.rules.map((rule) =>
                  rule.id === playbookItem.id
                    ? {
                        ...rule,
                        isLoading: false,
                        analysis: null,
                        paragraphs: [],
                        validationErrors: [{ field: "fullAnalysis", message: "Error running analysis" }],
                        isCanceled: true,
                      }
                    : rule
                ),
              }
            : classification
        )
      );
    } finally {
      Performance.endProcess(performanceProcessId);
    }
  };

  const getAnalysisClassification = async (playbookRuleId?: DocumentClassification["rules"][number]["id"]) => {
    if (!selectedPromptConfig) {
      log.error("No prompt configuration selected");
      return;
    }
    if (!selectedPlaybook) {
      return;
    }
    try {
      resetRiskAnalysisCanceledTokens();

      if (playbookRuleId) {
        const playbookRuleToUpdate = selectedPlaybook.rules.find((rule) => rule.id === playbookRuleId);
        setClassifications((prev) =>
          prev.map((classification) =>
            classification.playbookId === playbookRuleToUpdate.playbookId
              ? {
                  ...classification,
                  rules: classification.rules.map((rule) =>
                    rule.id === playbookRuleToUpdate.id
                      ? {
                          ...rule,
                          paragraphs: [],
                          analysis: null,
                          isLoading: true,
                          validationErrors: [],
                          isCanceled: false,
                          viewed: false,
                        }
                      : rule
                  ),
                }
              : classification
          )
        );

        await getDocumentParagraphs();

        trackEvent(AnalyticsKey.RISK_ANALYSIS.START, {
          userId: user.user.id,
          playbookRules: [playbookRuleToUpdate],
          prompts: selectedPromptConfig,
        });

        await runAnalysis(playbookRuleToUpdate);
      } else {
        const playbookRulesToUpdate = selectedPlaybook.rules;
        setClassifications((prev) =>
          prev.map((classification) =>
            classification.playbookId === selectedPlaybook.id
              ? {
                  ...classification,
                  rules: classification.rules.map((rule) => ({
                    ...rule,
                    analysis: null,
                    isLoading: true,
                    validationErrors: [],
                    isCanceled: false,
                    viewed: false,
                  })),
                }
              : classification
          )
        );
        trackEvent(AnalyticsKey.RISK_ANALYSIS.LABEL_PROMPTS, {
          userId: user.user.id,
          playbookRules: playbookRulesToUpdate,
          labelPrompts: selectedPromptConfig,
        });

        await getDocumentParagraphs();

        trackEvent(AnalyticsKey.RISK_ANALYSIS.START, {
          userId: user.user.id,
          playbookRules: playbookRulesToUpdate,
          prompts: selectedPromptConfig,
        });

        await Promise.all(playbookRulesToUpdate.map((rule) => runAnalysis(rule)));

        trackEvent(AnalyticsKey.RISK_ANALYSIS.FINISH, {
          userId: user.user.id,
          playbookRules: playbookRulesToUpdate,
          prompts: selectedPromptConfig,
          analysisResult: classifications,
        });
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        log.debug("Analysis cancelled by user");
        resetRiskAnalysisCanceledTokens();
      } else {
        log.error("Error classifying labels", undefined, error);
      }
    }
  };

  const refreshAnalysis = async (playbookRuleId?: DocumentClassification["rules"][number]["id"]) => {
    resetRiskAnalysisCanceledTokens();

    trackEvent(AnalyticsKey.USER.RISK_ANALYSIS.REFRESH, { userId: user.user.id });
    setErrors([]);

    // Clear applied redlines state when refreshing analysis
    if (playbookRuleId) {
      // Clear applied redlines only for the specific rule being refreshed
      const playbookRuleToUpdate = selectedPlaybook?.rules.find((rule) => rule.id === playbookRuleId);
      if (playbookRuleToUpdate) {
        window.dispatchEvent(
          new CustomEvent("clearAppliedRedlinesForClause", {
            detail: { clauseTopic: playbookRuleToUpdate.clauseTopic },
          })
        );
      }
    } else {
      // Clear all applied redlines when refreshing all rules
      window.dispatchEvent(new CustomEvent("clearAllAppliedRedlines"));
    }

    await getAnalysisClassification(playbookRuleId);
  };

  const handleInit = useCallback(async () => {
    if (!user?.user?.id) return;
    const playbooks = await fetchPlaybooks();
    if (!playbooks.length) return;
    const classificationFromStorage = await getFromDocumentStorage(DOCUMENT_STORAGE_KEY.CLASSIFICATIONS);
    const parsedClassifications: DocumentClassification[] = classificationFromStorage
      ? JSON.parse(classificationFromStorage)
      : [];
    const promptsFromStorage = await getFromDocumentStorage(DOCUMENT_STORAGE_KEY.PROMPTS_USED);
    const prompts: DocumentPrompts[] = promptsFromStorage ? JSON.parse(promptsFromStorage) : [];
    // for production deploy since the data structure changed verify if we need to clear the data
    if (!Array.isArray(parsedClassifications) || !Array.isArray(prompts)) {
      deleteAllClassificationDataFromDocumentStorage();
    }
    const updatedPlaybookIds = playbooks.map((playbook) => playbook.id);
    const parsedPlaybookIds = parsedClassifications.map((classification) => classification.playbookId);
    const isSomePlaybookDifferent = areArraysDifferent(updatedPlaybookIds, parsedPlaybookIds);
    if (parsedClassifications.length && !isSomePlaybookDifferent) {
      setClassifications(
        parsedClassifications.map((classification) => ({
          ...classification,
          rules: classification.rules.map((rule) => ({
            ...rule,
            isCanceled: rule.isLoading,
            isLoading: false,
          })),
        }))
      );
      setErrors([]);
      setPromptsUsed(prompts);
    } else {
      setClassifications(
        playbooks.map((playbook) => ({
          playbookId: playbook.id,
          rules: playbook.rules.map((rule) => ({
            id: rule.id,
            analysis: null,
            clauseTopic: rule.clauseTopic,
            isLoading: false,
            paragraphs: [],
            order: rule.order,
            playbookId: rule.playbookId,
            viewed: false,
            updatedAt: rule.updatedAt,
          })),
        }))
      );
      setPromptsUsed(
        playbooks.map((playbook) => ({
          playbookId: playbook.id,
          rules: playbook.rules.map((rule) => ({
            id: rule.id,
            clauseTopic: rule.clauseTopic,
            label: null,
            summary: null,
            fullAnalysis: null,
            valueExtraction: null,
            stoplight: null,
            redlines: null,
          })),
        }))
      );
    }
  }, [user?.user?.id]);

  const handleClearData = () => {
    setClassifications([]);
    setPromptsUsed([]);
    setPromptConfigs([]);
    setErrors([]);
    setPlaybooks([]);
    setSelectedPlaybook(undefined);
    setSelectedPromptConfig(null);
  };

  const handleCancel = async () => {
    Object.values(riskAnalysisCancelTokens).forEach((token) => token.cancel());
    if (!selectedPlaybook) return;
    // only cancel the ones that are loading
    setClassifications((prev) => {
      const updatedClassifications = prev.map((classification) =>
        classification.playbookId === selectedPlaybook.id
          ? {
              ...classification,
              rules: classification.rules.map((rule) =>
                rule.isLoading
                  ? {
                      ...rule,
                      paragraphs: [],
                      analysis: null,
                      isLoading: false,
                      validationErrors: [],
                      isCanceled: true,
                    }
                  : rule
              ),
            }
          : classification
      );
      saveToDocumentStorage(DOCUMENT_STORAGE_KEY.CLASSIFICATIONS, JSON.stringify(updatedClassifications));
      return updatedClassifications;
    });
  };

  useEffect(() => {
    fetchPromptConfigs();
  }, [fetchPromptConfigs]);

  useEffect(() => {
    handleInit();
  }, [handleInit]);

  return (
    <DocumentClassificationContext.Provider
      value={{
        errors,
        classifications,
        setClassifications,
        refreshAnalysis,
        promptsUsed,
        getAnalysisClassification,
        promptConfigs,
        selectedPromptConfig,
        setSelectedPromptConfig,
        fetchPromptConfigs,
        isFetchingPromptConfigs,
        handleClearData,
        handleCancel,
        playbooks,
        setPlaybooks,
        sortPlaybooksBy,
        setSortPlaybooksBy,
        sortPlaybooks,
        fetchPlaybooks,
        fetchingPlaybooks,
        selectedPlaybook,
        setSelectedPlaybook,
        currentClassification,
        addPlaybook,
        copyPlaybook,
        editPlaybook,
        removePlaybook,
      }}
    >
      {children}
    </DocumentClassificationContext.Provider>
  );
};

export const useDocumentClassification = () => {
  const context = useContext(DocumentClassificationContext);

  if (!context) {
    throw new Error("useDocumentClassification must be used within a DocumentClassificationProvider");
  }

  return context;
};
